import {
  applyColumnPreferenceSet,
  type ColumnPreferenceColumn,
  COLUMN_PREFERENCES_KEY,
  createColumnPreferenceSet,
  LoadingIndicator,
  replaceColumnPreferenceSet,
  resolveColumnPreferenceSet,
  sanitizeColumnPreferences,
  selectPreference,
  updatePreference,
  getQepSystem,
} from '@qep/shared/src';
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Close from '@mui/icons-material/Close';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import Lock from '@mui/icons-material/Lock';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import {useEffect, useMemo, useState} from 'react';
import {
  type ColumnPreferenceApp,
  columnPreferenceApps,
  columnPreferenceAppsById,
} from './column-preferences-catalog';
import './column-preferences.scss';

type ColumnListName = 'available' | 'current';

type ColumnLayout = {
  available: ColumnPreferenceColumn[];
  current: ColumnPreferenceColumn[];
};

type DragState = {
  columnId: string;
  sourceList: ColumnListName;
};

type DropTarget = {
  list: ColumnListName;
  columnId?: string;
};

type SortMode = 'default' | 'asc' | 'desc';

const legacyOrderEntryAppId = 'orderEntry';
const legacyOrderEntryFeature = 'orderEntry1.0';
const invalidDefaultColumnMessage = 'At least one default column is required.';
const lockedColumnIdsByAppId: Record<string, string[]> = {
  orderEntry: ['direction', 'instrument', 'quantity', 'strategy'],
};

function cloneColumn(column: ColumnPreferenceColumn): ColumnPreferenceColumn {
  return {...column};
}

function cloneColumns(columns: readonly ColumnPreferenceColumn[]) {
  return columns.map(column => cloneColumn(column));
}

function getLockedColumnIds(appId: string) {
  return lockedColumnIdsByAppId[appId] ?? [];
}

function isLockedColumn(appId: string, columnId: string) {
  return getLockedColumnIds(appId).includes(columnId);
}

/**
 * Returns the preference apps that are available in the current environment
 * without changing the persisted catalog or any stored legacy preferences.
 */
function getVisibleColumnPreferenceApps() {
  const isLegacyOrderEntryEnabled = getQepSystem().container.canUseFeature(legacyOrderEntryFeature);

  return columnPreferenceApps.filter(app => app.id !== legacyOrderEntryAppId || isLegacyOrderEntryEnabled);
}

/**
 * Selects the first visible preference app so a hidden legacy app is never
 * opened by default outside the environments where it is available.
 */
function getDefaultSelectedAppId(apps: readonly ColumnPreferenceApp[]) {
  return apps[0]?.id ?? '';
}

export function normalizeLayout(app: ColumnPreferenceApp, layout: ColumnLayout): ColumnLayout {
  const lockedColumnIds = getLockedColumnIds(app.id);
  if (lockedColumnIds.length === 0) {
    return {
      available: cloneColumns(layout.available),
      current: cloneColumns(layout.current),
    };
  }

  const lockedColumnIdSet = new Set(lockedColumnIds);
  const defaultColumnsById = new Map(app.columns.map(column => [column.id, column]));
  const currentColumnsById = new Map(layout.current.map(column => [column.id, column]));
  const availableColumnsById = new Map(layout.available.map(column => [column.id, column]));
  const missingAvailableColumns = app.columns
    .filter(column => !lockedColumnIdSet.has(column.id) && !currentColumnsById.has(column.id) && !availableColumnsById.has(column.id))
    .map(column => cloneColumn(column));

  return {
    current: [
      ...lockedColumnIds
        .map(columnId => currentColumnsById.get(columnId) ?? availableColumnsById.get(columnId) ?? defaultColumnsById.get(columnId))
        .filter((column): column is ColumnPreferenceColumn => column !== undefined)
        .map(column => ({...cloneColumn(column), visible: true})),
      ...layout.current.filter(column => !lockedColumnIdSet.has(column.id)).map(column => cloneColumn(column)),
    ],
    available: [
      ...layout.available
        .filter(column => !lockedColumnIdSet.has(column.id))
        .map(column => cloneColumn(column)),
      ...missingAvailableColumns,
    ],
  };
}

function getDefaultLayout(app: ColumnPreferenceApp): ColumnLayout {
  return normalizeLayout(app, {
    available: cloneColumns(app.columns.filter(column => !column.visible)),
    current: cloneColumns(app.columns.filter(column => column.visible)),
  });
}

export function buildColumnLayout(
  preferences: ReturnType<typeof sanitizeColumnPreferences>,
  appId: string,
): ColumnLayout {
  const app = columnPreferenceAppsById[appId];
  if (!app) {
    return {available: [], current: []};
  }

  const resolvedColumns = applyColumnPreferenceSet(
    app.columns,
    resolveColumnPreferenceSet(preferences, appId),
  );

  return normalizeLayout(app, {
    available: cloneColumns(resolvedColumns.filter(column => !column.visible)),
    current: cloneColumns(resolvedColumns.filter(column => column.visible)),
  });
}

export function applyColumnLayout(
  preferences: ReturnType<typeof sanitizeColumnPreferences>,
  appId: string,
  layout: ColumnLayout,
) {
  const app = columnPreferenceAppsById[appId];
  let normalizedLayout: ColumnLayout;
  if (app) {
    normalizedLayout = normalizeLayout(app, layout);
  } else {
    normalizedLayout = {
      available: cloneColumns(layout.available),
      current: cloneColumns(layout.current),
    };
  }

  const nextColumns = [
    ...normalizedLayout.current.map(column => ({...column, visible: true})),
    ...normalizedLayout.available.map(column => ({...column, visible: false})),
  ];

  return replaceColumnPreferenceSet(
    preferences,
    appId,
    createColumnPreferenceSet(nextColumns, appId),
  );
}

function sortColumns(columns: readonly ColumnPreferenceColumn[]) {
  return cloneColumns(columns).sort((firstColumn, secondColumn) => firstColumn.label.localeCompare(secondColumn.label));
}

function getNextSortMode(sortMode: SortMode): SortMode {
  switch (sortMode) {
    case 'default': {
      return 'asc';
    }

    case 'asc': {
      return 'desc';
    }

    case 'desc': {
      return 'default';
    }
  }
}

export function sortColumnsByMode(
  columns: readonly ColumnPreferenceColumn[],
  sortMode: SortMode,
  defaultColumns: readonly ColumnPreferenceColumn[],
) {
  if (sortMode === 'asc') {
    return sortColumns(columns);
  }

  if (sortMode === 'desc') {
    return sortColumns(columns).reverse();
  }

  const columnOrder = new Map(defaultColumns.map((column, index) => [column.id, index]));
  return cloneColumns(columns).sort((firstColumn, secondColumn) => (columnOrder.get(firstColumn.id) ?? 0) - (columnOrder.get(secondColumn.id) ?? 0));
}

function getAppButtonTooltipTitle(isInvalid: boolean, hasUnsavedChanges: boolean) {
  if (isInvalid) {
    return invalidDefaultColumnMessage;
  }

  return hasUnsavedChanges ? 'Unsaved Changes' : '';
}

function getCurrentRowClassName(
  isDimmed: boolean,
  isLocked: boolean,
  isDropTarget: boolean,
) {
  return `column-preferences__row column-preferences__row--current ${isDimmed ? 'is-dimmed' : ''} ${isLocked ? 'is-locked' : ''} ${isDropTarget ? 'is-drop-target' : ''}`;
}

function insertColumn(
  columns: readonly ColumnPreferenceColumn[],
  column: ColumnPreferenceColumn,
  targetColumnId?: string,
) {
  const nextColumns = cloneColumns(columns);
  const targetIndex = targetColumnId === undefined
    ? -1
    : nextColumns.findIndex(currentColumn => currentColumn.id === targetColumnId);

  if (targetIndex === -1) {
    nextColumns.push(cloneColumn(column));
  } else {
    nextColumns.splice(targetIndex, 0, cloneColumn(column));
  }

  return nextColumns;
}

function moveColumn(
  layout: ColumnLayout,
  sourceList: ColumnListName,
  targetList: ColumnListName,
  columnId: string,
  targetColumnId?: string,
): ColumnLayout {
  if (sourceList === targetList && columnId === targetColumnId) {
    return {
      available: cloneColumns(layout.available),
      current: cloneColumns(layout.current),
    };
  }

  const sourceColumns = cloneColumns(layout[sourceList]);
  const targetColumns = sourceList === targetList ? sourceColumns : cloneColumns(layout[targetList]);
  const sourceIndex = sourceColumns.findIndex(column => column.id === columnId);
  if (sourceIndex === -1) {
    return {
      available: cloneColumns(layout.available),
      current: cloneColumns(layout.current),
    };
  }

  const [movedColumn] = sourceColumns.splice(sourceIndex, 1);

  if (sourceList === targetList) {
    return {
      ...layout,
      [sourceList]: insertColumn(sourceColumns, movedColumn, targetColumnId),
    };
  }

  return {
    ...layout,
    [sourceList]: sourceColumns,
    [targetList]: insertColumn(targetColumns, movedColumn, targetColumnId),
  };
}

function matchesFilter(column: ColumnPreferenceColumn, searchText: string) {
  return column.label.toLowerCase().includes(searchText.trim().toLowerCase());
}

export function ColumnPreferences() {
  const {preference, isLoading} = selectPreference(COLUMN_PREFERENCES_KEY);
  const mutation = updatePreference(COLUMN_PREFERENCES_KEY);
  const savedPreferences = useMemo(() => sanitizeColumnPreferences(preference), [preference]);
  const [draftPreferences, setDraftPreferences] = useState(savedPreferences);
  const visibleColumnPreferenceApps = getVisibleColumnPreferenceApps();
  const [selectedAppId, setSelectedAppId] = useState(() => getDefaultSelectedAppId(visibleColumnPreferenceApps));
  const [searchText, setSearchText] = useState('');
  const [dragState, setDragState] = useState<DragState>();
  const [dropTarget, setDropTarget] = useState<DropTarget>();
  const [availableSortMode, setAvailableSortMode] = useState<SortMode>('default');
  const [currentSortMode, setCurrentSortMode] = useState<SortMode>('default');

  useEffect(() => {
    setDraftPreferences(savedPreferences);
  }, [savedPreferences]);

  const activeApp = visibleColumnPreferenceApps.find(app => app.id === selectedAppId) ?? visibleColumnPreferenceApps[0];
  const activeAppId = activeApp?.id ?? '';
  const activeAppColumns = activeApp?.columns ?? [];
  const layout = useMemo(
    () => activeApp ? buildColumnLayout(draftPreferences, activeApp.id) : {available: [], current: []},
    [activeApp, draftPreferences],
  );
  const normalizedSearchText = searchText.trim().toLowerCase();
  const filteredAvailableColumns = useMemo(() => {
    if (normalizedSearchText === '') {
      return layout.available;
    }

    return layout.available.filter(column => matchesFilter(column, normalizedSearchText));
  }, [layout.available, normalizedSearchText]);
  const filteredCurrentColumns = useMemo(() => {
    if (normalizedSearchText === '') {
      return layout.current;
    }

    return layout.current.filter(column => matchesFilter(column, normalizedSearchText));
  }, [layout.current, normalizedSearchText]);
  const filteredRemovableCurrentColumns = useMemo(() => filteredCurrentColumns
    .filter(column => !isLockedColumn(activeAppId, column.id)), [activeAppId, filteredCurrentColumns]);
  const unsavedAppIds = useMemo(() => new Set(visibleColumnPreferenceApps
    .filter(app => JSON.stringify(buildColumnLayout(draftPreferences, app.id)) !== JSON.stringify(buildColumnLayout(savedPreferences, app.id)))
    .map(app => app.id)), [draftPreferences, savedPreferences, visibleColumnPreferenceApps]);
  const invalidAppIds = useMemo(() => new Set(visibleColumnPreferenceApps
    .filter(app => buildColumnLayout(draftPreferences, app.id).current.length === 0)
    .map(app => app.id)), [draftPreferences, visibleColumnPreferenceApps]);
  const persistedState = JSON.stringify(savedPreferences);
  const draftState = JSON.stringify(draftPreferences);
  const isDirty = persistedState !== draftState;
  const hasValidationErrors = invalidAppIds.size > 0;

  const updateLayout = (updater: (currentLayout: ColumnLayout) => ColumnLayout) => {
    if (!activeApp) {
      return;
    }

    setDraftPreferences(currentPreferences => applyColumnLayout(
      currentPreferences,
      activeApp.id,
      updater(buildColumnLayout(currentPreferences, activeApp.id)),
    ));
  };

  const moveColumnBetweenLists = (sourceList: ColumnListName, targetList: ColumnListName, columnId: string, targetColumnId?: string) => {
    if (sourceList === 'available' || targetList === 'available') {
      setAvailableSortMode('default');
    }

    if (sourceList === 'current' || targetList === 'current') {
      setCurrentSortMode('default');
    }

    updateLayout(currentLayout => moveColumn(currentLayout, sourceList, targetList, columnId, targetColumnId));
  };

  const handleListDrop = (targetList: ColumnListName) => {
    if (!dragState) {
      return;
    }

    moveColumnBetweenLists(dragState.sourceList, targetList, dragState.columnId, dropTarget?.list === targetList ? dropTarget.columnId : undefined);
    setDragState(undefined);
    setDropTarget(undefined);
  };

  const handleDefaultReset = () => {
    if (!activeApp) {
      return;
    }

    setAvailableSortMode('default');
    setCurrentSortMode('default');
    setDraftPreferences(currentPreferences => applyColumnLayout(currentPreferences, activeApp.id, getDefaultLayout(activeApp)));
  };

  const getSortEndIcon = (sortMode: SortMode) => (
    <span className='column-preferences__sort-icon' aria-hidden='true'>
      {sortMode === 'asc' && <ArrowUpward fontSize='inherit' />}
      {sortMode === 'desc' && <ArrowDownward fontSize='inherit' />}
    </span>
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div className='column-preferences'>
      <Stack spacing={2} className='column-preferences__content'>
        <div>
          <Typography variant='h6'>Columns</Typography>
          <Typography
            variant='body2'
            className='column-preferences__subtitle'
            sx={{fontSize: '11px', lineHeight: 1.4}}
          >
            Choose an app, then drag or double-click columns between the lists to build the default layout. Saved changes apply to newly opened app screens.
          </Typography>
        </div>

        <hr className='column-preferences__divider' aria-hidden='true' />

        <div className='column-preferences__app-grid' aria-label='Column preference apps'>
          {visibleColumnPreferenceApps.map(app => {
            const hasUnsavedChanges = unsavedAppIds.has(app.id);
            const isInvalid = invalidAppIds.has(app.id);
            const tooltipTitle = getAppButtonTooltipTitle(isInvalid, hasUnsavedChanges);
            const appButton = (
              <button
                key={app.id}
                type='button'
                className={`column-preferences__app-button ${activeApp?.id === app.id ? 'selected' : ''} ${isInvalid ? 'invalid' : ''}`}
                aria-label={`${app.label} columns`}
                aria-pressed={activeApp?.id === app.id}
                onClick={() => {
                  setSelectedAppId(app.id);
                  setSearchText('');
                  setDragState(undefined);
                  setDropTarget(undefined);
                  setAvailableSortMode('default');
                  setCurrentSortMode('default');
                }}
              >
                <span className='column-preferences__app-button-title'>{app.label}</span>
                {hasUnsavedChanges && (
                  <span className='column-preferences__app-button-indicator' aria-label={`${app.label} has unsaved changes`}>
                    <FiberManualRecord fontSize='inherit' />
                  </span>
                )}
              </button>
            );

            if (tooltipTitle === '') {
              return appButton;
            }

            return (
              <Tooltip
                key={app.id}
                title={tooltipTitle}
                placement='top'
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <span className='column-preferences__app-button-tooltip'>{appButton}</span>
              </Tooltip>
            );
          })}
        </div>

        <div className='column-preferences__builder'>
          <section className='column-preferences__panel'>
            <Typography
              variant='subtitle2'
              className='column-preferences__panel-title'
              sx={{fontSize: '11px'}}
            >
              Available Columns
            </Typography>
            <div className='column-preferences__panel-controls'>
              <Button
                size='small'
                variant='text'
                endIcon={getSortEndIcon(availableSortMode)}
                className='column-preferences__sort-button'
                aria-label='Cycle available columns sort order'
                onClick={() => {
                  const nextSortMode = getNextSortMode(availableSortMode);
                  setAvailableSortMode(nextSortMode);
                  updateLayout(currentLayout => ({
                    ...currentLayout,
                    available: sortColumnsByMode(currentLayout.available, nextSortMode, activeAppColumns),
                  }));
                }}
              >
                Sort
              </Button>
              <TextField
                size='small'
                label='Filter'
                value={searchText}
                onChange={event => {
                  setSearchText(event.target.value);
                }}
                className='column-preferences__filter'
                InputProps={normalizedSearchText === ''
                  ? undefined
                  : {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='Clear filter'
                          edge='end'
                          size='small'
                          onClick={() => {
                            setSearchText('');
                          }}
                        >
                          <Close fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
              />
            </div>

            <div className='column-preferences__list-shell'>
              <div
                className={`column-preferences__list ${dropTarget?.list === 'available' && !dropTarget.columnId ? 'is-drop-target-end' : ''}`}
                data-testid='column-preferences-available-list'
                onDragOver={event => {
                  event.preventDefault();
                  if (event.target === event.currentTarget) {
                    setDropTarget({list: 'available'});
                  }
                }}
                onDrop={event => {
                  event.preventDefault();
                  handleListDrop('available');
                }}
              >
                {filteredAvailableColumns.length === 0
                  ? (
                    <div className='column-preferences__empty'>
                      <Typography variant='body2'>
                        {normalizedSearchText === '' ? 'No available columns.' : 'No available columns match this filter.'}
                      </Typography>
                    </div>
                  )
                  : filteredAvailableColumns.map(column => (
                    <div
                      key={column.id}
                      className={`column-preferences__row ${dropTarget?.list === 'available' && dropTarget.columnId === column.id ? 'is-drop-target' : ''}`}
                      draggable
                      onDoubleClick={() => {
                        moveColumnBetweenLists('available', 'current', column.id);
                      }}
                      onDragStart={event => {
                        event.dataTransfer.effectAllowed = 'move';
                        setDragState({columnId: column.id, sourceList: 'available'});
                      }}
                      onDragEnd={() => {
                        setDragState(undefined);
                        setDropTarget(undefined);
                      }}
                      onDragEnter={() => {
                        setDropTarget({list: 'available', columnId: column.id});
                      }}
                      onDragOver={event => {
                        event.preventDefault();
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!dragState) {
                          return;
                        }

                        moveColumnBetweenLists(dragState.sourceList, 'available', dragState.columnId, column.id);
                        setDragState(undefined);
                        setDropTarget(undefined);
                      }}
                    >
                      <span className='column-preferences__row-label'>{column.label}</span>
                    </div>
                  ))}
              </div>
            </div>
          </section>

          <div className='column-preferences__transfer-indicator' aria-hidden='true'>
            <SwapHoriz fontSize='inherit' />
          </div>

          <section className='column-preferences__panel'>
            <Typography
              variant='subtitle2'
              className='column-preferences__panel-title'
              sx={{fontSize: '11px'}}
            >
              Current Layout
            </Typography>
            <div className='column-preferences__panel-controls'>
              <Button
                size='small'
                variant='outlined'
                disabled={normalizedSearchText === '' ? layout.available.length === 0 : filteredAvailableColumns.length === 0}
                onClick={() => {
                  setAvailableSortMode('default');
                  setCurrentSortMode('default');
                  updateLayout(currentLayout => {
                    const columnsToAdd = normalizedSearchText === ''
                      ? currentLayout.available
                      : currentLayout.available.filter(column => matchesFilter(column, normalizedSearchText));

                    if (columnsToAdd.length === 0) {
                      return {
                        available: cloneColumns(currentLayout.available),
                        current: cloneColumns(currentLayout.current),
                      };
                    }

                    return {
                      available: normalizedSearchText === ''
                        ? []
                        : currentLayout.available
                          .filter(column => !matchesFilter(column, normalizedSearchText))
                          .map(column => cloneColumn(column)),
                      current: [
                        ...cloneColumns(currentLayout.current),
                        ...cloneColumns(columnsToAdd),
                      ],
                    };
                  });
                }}
              >
                {normalizedSearchText === '' ? 'Add All' : 'Add Filtered'}
              </Button>
              <Button
                size='small'
                variant='outlined'
                disabled={normalizedSearchText === '' ? layout.current.filter(column => !isLockedColumn(activeAppId, column.id)).length === 0 : filteredRemovableCurrentColumns.length === 0}
                onClick={() => {
                  setAvailableSortMode('default');
                  setCurrentSortMode('default');
                  updateLayout(currentLayout => {
                    const shouldRemoveColumn = (column: ColumnPreferenceColumn) => !isLockedColumn(activeAppId, column.id)
                      && (normalizedSearchText === '' || matchesFilter(column, normalizedSearchText));
                    const columnsToRemove = currentLayout.current.filter(column => shouldRemoveColumn(column));

                    if (columnsToRemove.length === 0) {
                      return {
                        available: cloneColumns(currentLayout.available),
                        current: cloneColumns(currentLayout.current),
                      };
                    }

                    return {
                      available: [
                        ...cloneColumns(currentLayout.available),
                        ...cloneColumns(columnsToRemove),
                      ],
                      current: currentLayout.current
                        .filter(column => !shouldRemoveColumn(column))
                        .map(column => cloneColumn(column)),
                    };
                  });
                }}
              >
                {normalizedSearchText === '' ? 'Remove All' : 'Remove Filtered'}
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={handleDefaultReset}
              >
                Default
              </Button>
            </div>

            <div className='column-preferences__list-shell'>
              <div
                className={`column-preferences__list column-preferences__list--current ${dropTarget?.list === 'current' && !dropTarget.columnId ? 'is-drop-target-end' : ''}`}
                data-testid='column-preferences-current-list'
                onDragOver={event => {
                  event.preventDefault();
                  if (event.target === event.currentTarget) {
                    setDropTarget({list: 'current'});
                  }
                }}
                onDrop={event => {
                  event.preventDefault();
                  handleListDrop('current');
                }}
              >
                {layout.current.length === 0
                  ? (
                    <div className='column-preferences__empty'>
                      <Typography variant='body2'>No columns are currently selected.</Typography>
                    </div>
                  )
                  : layout.current.map(column => {
                    const isDimmed = normalizedSearchText !== '' && !matchesFilter(column, normalizedSearchText);
                    const isLocked = isLockedColumn(activeAppId, column.id);
                    const isDropTarget = dropTarget?.list === 'current' && dropTarget.columnId === column.id;

                    return (
                      <div
                        key={column.id}
                        className={getCurrentRowClassName(isDimmed, isLocked, isDropTarget)}
                        draggable={!isLocked}
                        onDoubleClick={() => {
                          if (!isLocked) {
                            moveColumnBetweenLists('current', 'available', column.id);
                          }
                        }}
                        onDragStart={event => {
                          if (isLocked) {
                            event.preventDefault();
                            return;
                          }

                          event.dataTransfer.effectAllowed = 'move';
                          setDragState({columnId: column.id, sourceList: 'current'});
                        }}
                        onDragEnd={() => {
                          setDragState(undefined);
                          setDropTarget(undefined);
                        }}
                        onDragEnter={() => {
                          if (!isLocked) {
                            setDropTarget({list: 'current', columnId: column.id});
                          }
                        }}
                        onDragOver={event => {
                          event.preventDefault();
                        }}
                        onDrop={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (!dragState || isLocked) {
                            return;
                          }

                          moveColumnBetweenLists(dragState.sourceList, 'current', dragState.columnId, column.id);
                          setDragState(undefined);
                          setDropTarget(undefined);
                        }}
                      >
                        <span className='column-preferences__row-label'>{column.label}</span>
                        {isLocked && (
                          <span className='column-preferences__row-lock' aria-label='Locked column'>
                            <Lock fontSize='inherit' />
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </section>
        </div>

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            variant='text'
            disabled={!isDirty || mutation.isPending}
            onClick={() => {
              setDraftPreferences(savedPreferences);
            }}
          >
            Discard
          </Button>
          <Button
            variant='contained'
            disabled={!isDirty || mutation.isPending || hasValidationErrors}
            onClick={() => {
              mutation.mutate(draftPreferences);
            }}
          >
            Save changes
          </Button>
        </Stack>
      </Stack>
    </div>
  );
}
